import { useQueryClient } from '@tanstack/react-query';
import { components, paths } from 'src/generated/v1';
import { $api, $apiFetchClient } from './api';

export type ReturnPickingCreate = components['schemas']['ReturnPickingCreate'];
export type Address = components['schemas']['Address'];
export type ReturnPicking = components['schemas']['ReturnPicking'];
export type ReturnLabel = components['schemas']['ReturnLabel'];
export type ReturnLabelInfo = components['schemas']['ReturnLabelInfo'];

export async function downloadReturnLabel(returnPickingId: number): Promise<ReturnLabel> {
  const { data, error } = await $apiFetchClient.GET('/return_picking/{return_picking_id}/return_label', {
    params: { path: { return_picking_id: returnPickingId } },
  });
  if (error || !data) {
    throw error instanceof Error ? error : new Error(`return label request failed: ${JSON.stringify(error ?? null)}`);
  }
  return data;
}

export function saveBase64File(label: ReturnLabel) {
  const bytes = Uint8Array.from(atob(label.content_base64), c => c.charCodeAt(0));
  const blob = new Blob([bytes], { type: label.mimetype || 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = label.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['get', '/return_picking/{return_picking_id}'] });
  queryClient.invalidateQueries({ queryKey: ['get', '/return_picking/quantities/{sale_order_id}'] });
  queryClient.invalidateQueries({ queryKey: ['get', '/return_pickings'] });
}

export const useReturnCreate = () => {
  const queryClient = useQueryClient();

  const createMutation = $api.useMutation('post', '/return_picking/return_picking/{sale_order_id}', {
    onSuccess() {
      invalidate(queryClient);
    },
  });

  return createMutation;
};

export const useReturnsQuery = (params: paths['/return_pickings']['get']['parameters']['query']) => {
  return $api.useQuery('get', '/return_pickings', {
    params: {
      query: params,
    },
  });
};

export const useSingleReturnQuery = (id: number) => {
  return $api.useQuery('get', '/return_picking/{return_picking_id}', {
    params: {
      path: {
        return_picking_id: id,
      },
    },
  });
};

export const useReturnPickingProductsQuery = (
  id: number,
  options?: {
    enabled?: boolean;
  },
) => {
  return $api.useQuery('get', '/return_picking/quantities/{sale_order_id}', {
    params: {
      path: {
        sale_order_id: id,
      },
    },
    options,
  });
};

export const useUpdateRefundedState = () => {
  const queryClient = useQueryClient();

  return $api.useMutation('put', '/return_picking/return_picking/{return_picking_id}', {
    onSuccess() {
      invalidate(queryClient);
    },
  });
};

export const useReturnReasonsQuery = () => {
  return $api.useQuery('get', '/stock/inventory/return_reasons', {});
};
